import { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowLeft, MapPin, QrCode, ExternalLink, UtensilsCrossed,
  Phone, Truck, Info, Search, MessageCircle,
} from "lucide-react";
import { openWhatsApp, DEFAULT_GREETING } from "./lib/whatsapp";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const qrSrc = (text) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&bgcolor=0a0604&color=f4d089&data=${encodeURIComponent(text)}`;

const Section = ({ children, className = "" }) => (
  <section className={`max-w-[1280px] mx-auto px-5 sm:px-8 ${className}`}>{children}</section>
);

const Card = ({ children, className = "" }) => (
  <div className={`rounded-xl border border-[#3a2a14]/70 bg-gradient-to-b from-[#14100a] to-[#0d0905] ${className}`}>
    {children}
  </div>
);

const GoldButton = ({ children, className = "", ...props }) => (
  <button
    className={`bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold tracking-wide px-5 py-2.5 rounded-md hover:from-[#f1d59c] hover:to-[#d4ac6f] transition-all shadow-[0_4px_20px_rgba(212,175,116,0.25)] ${className}`}
    {...props}
  >
    {children}
  </button>
);

function BranchFoodCard({ branch, cityName }) {
  const hasMenu = !!branch.petpooja_menu_url;
  return (
    <Card className="p-5" data-testid={`food-branch-${branch.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#a89878]">{cityName}</div>
          <h3 className="font-display text-white text-2xl mt-1">{branch.name}</h3>
          {branch.address && (
            <div className="flex items-center gap-2 text-[#bfb393] text-sm mt-2">
              <MapPin size={14} className="text-[#d4a574]" />
              <span>{branch.address}</span>
            </div>
          )}
          {branch.food_phone && (
            <div className="flex items-center gap-2 text-[#bfb393] text-sm mt-1.5">
              <Phone size={14} className="text-[#d4a574]" />
              <a href={`tel:${branch.food_phone}`} className="hover:text-[#f4d089]">
                {branch.food_phone}
              </a>
            </div>
          )}
          <div className="mt-5 flex flex-wrap gap-2">
            {hasMenu ? (
              <>
                <GoldButton
                  data-testid={`open-menu-${branch.id}`}
                  onClick={() => window.open(branch.petpooja_menu_url, "_blank", "noopener")}
                  className="text-xs flex items-center gap-2"
                >
                  <ExternalLink size={14} /> OPEN MENU
                </GoldButton>
                <button
                  onClick={() => window.open(branch.petpooja_menu_url, "_blank", "noopener")}
                  className="text-xs border border-[#d4a574]/40 rounded-md px-4 py-2.5 text-[#f4d089] hover:bg-[#1a0f06] flex items-center gap-2"
                >
                  <QrCode size={14} /> ORDER ON BILLING SOFTWARE
                </button>
              </>
            ) : (
              <div className="text-xs text-[#a89878] italic flex items-center gap-2">
                <Info size={14} /> Menu link coming soon — please ask staff for the QR.
              </div>
            )}
          </div>
        </div>
        <div className="shrink-0 hidden sm:block">
          {hasMenu ? (
            <div className="text-center" data-testid={`qr-${branch.id}`}>
              <div className="bg-[#0a0604] border border-[#d4a574]/40 rounded-lg p-2">
                <img
                  src={qrSrc(branch.petpooja_menu_url)}
                  alt={`${branch.name} menu QR`}
                  width={140}
                  height={140}
                  className="rounded"
                />
              </div>
              <div className="text-[10px] tracking-[0.16em] text-[#a89878] mt-2">SCAN TO ORDER</div>
            </div>
          ) : (
            <div className="w-[140px] h-[140px] rounded-lg border border-dashed border-[#3a2a14] flex items-center justify-center text-[#3a2a14]">
              <QrCode size={48} />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function Food() {
  const [cities, setCities] = useState([]);
  const [branches, setBranches] = useState([]);
  const [cityId, setCityId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/cities`).then((r) => r.data).catch(() => []),
      axios.get(`${API}/branches`).then((r) => r.data).catch(() => []),
    ]).then(([c, b]) => {
      setCities(c);
      setBranches(b);
      setLoading(false);
    });
  }, []);

  const cityName = (id) => cities.find((c) => c.id === id)?.name || "—";

  const visible = branches.filter((b) => {
    if (cityId && b.city_id !== cityId) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        b.name.toLowerCase().includes(s) ||
        (b.address || "").toLowerCase().includes(s) ||
        cityName(b.city_id).toLowerCase().includes(s)
      );
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-[#0a0604] text-white" data-testid="cinevibe-food">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-[#0a0604]/85 border-b border-[#d4a574]/15">
        <Section className="flex items-center justify-between py-4">
          <button
            onClick={() => (window.location.href = "/")}
            className="flex items-center gap-2 text-[#d8cdb3] hover:text-[#f4d089]"
            data-testid="food-back-home"
          >
            <ArrowLeft size={18} /> <span className="text-sm tracking-[0.16em]">BACK TO HOME</span>
          </button>
          <div className="flex items-center gap-2 text-[#f4d089]">
            <UtensilsCrossed size={18} />
            <span className="font-display tracking-[0.18em] text-sm">ORDER FOOD</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => openWhatsApp(DEFAULT_GREETING)}
              data-testid="food-header-whatsapp"
              className="hidden sm:flex items-center gap-2 text-[#7ad99c] hover:text-[#a3eebd] text-sm"
            >
              <MessageCircle size={16} /> WhatsApp
            </button>
            <GoldButton
              data-testid="food-book-now"
              onClick={() => (window.location.href = "/book")}
              className="text-xs tracking-[0.16em]"
            >
              BOOK SCREENING
            </GoldButton>
          </div>
        </Section>
      </header>

      {/* Hero */}
      <Section className="pt-10 pb-6">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 items-end">
          <div>
            <div className="text-[#f4d089] tracking-[0.22em] text-xs">THE CINEVIBE CAFÉ · POWERED BY BILLING SOFTWARE</div>
            <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-3">
              In-room dining,<br />
              <span className="text-[#f4d089]">your way.</span>
            </h1>
            <p className="mt-4 text-[#bfb393] max-w-xl">
              Scan the QR for your branch, browse the live café menu, and order directly from
              Billing Software. We handle the screening — the kitchen handles the food.
            </p>
            <div className="mt-5 flex flex-wrap gap-3 text-xs">
              <span className="border border-[#7ad99c]/40 text-[#7ad99c] rounded-full px-3 py-1.5 flex items-center gap-1.5">
                <Truck size={12} /> Delivered to your private room
              </span>
              <span className="border border-[#d4a574]/40 text-[#f4d089] rounded-full px-3 py-1.5 flex items-center gap-1.5">
                <Info size={12} /> Food billed separately by café (GST 5%)
              </span>
            </div>
          </div>
          <Card className="p-5">
            <h3 className="text-[#f4d089] tracking-[0.16em] text-xs font-semibold mb-4">HOW IT WORKS</h3>
            <ol className="space-y-3 text-sm">
              {[
                "Pick your branch below.",
                "Scan the QR or tap Open Menu.",
                "Order on Billing Software — kitchen receives instantly.",
                "Food delivered fresh to your private room.",
              ].map((s, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full border border-[#d4a574]/40 text-[#f4d089] text-xs flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-[#d8cdb3]">{s}</span>
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </Section>

      {/* Filters */}
      <Section className="pt-6">
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-3">
            <select
              data-testid="food-city-filter"
              value={cityId}
              onChange={(e) => setCityId(e.target.value)}
              className="bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2.5 text-sm text-[#d8cdb3]"
            >
              <option value="">All Cities</option>
              {cities.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <div className="flex items-center gap-2 bg-[#0a0604] border border-[#3a2a14] rounded-md px-3">
              <Search size={14} className="text-[#a89878]" />
              <input
                data-testid="food-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by branch or area…"
                className="flex-1 bg-transparent py-2.5 text-sm text-white outline-none placeholder:text-[#a89878]"
              />
            </div>
          </div>
        </Card>
      </Section>

      {/* Branch list */}
      <Section className="py-8">
        {loading ? (
          <div className="text-center text-[#a89878] py-16" data-testid="food-loading">Loading branches…</div>
        ) : visible.length === 0 ? (
          <div className="text-center text-[#a89878] py-16" data-testid="food-empty">
            No branches match your filters yet.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-5" data-testid="food-branches-grid">
            {visible.map((b) => (
              <BranchFoodCard key={b.id} branch={b} cityName={cityName(b.city_id)} />
            ))}
          </div>
        )}
      </Section>

      {/* Footer note */}
      <Section className="pb-12">
        <Card className="p-5 text-center">
          <div className="text-[#f4d089] tracking-[0.18em] text-xs font-semibold">SEPARATE BILLING — FULL TRANSPARENCY</div>
          <p className="text-[#bfb393] text-sm mt-2 max-w-2xl mx-auto">
            Screening charges (GST 18%) are billed by The CineVibe. Food &amp; beverages (GST 5%) are
            billed by the café via Billing Software. You receive two separate invoices for clarity.
          </p>
        </Card>
      </Section>
    </div>
  );
}
